/**
 * Monaco Editor DSL language support
 *
 * Provides language definition, autocomplete, and snippets for QuantQuack DSL
 */
import { BUILTIN_FUNCTIONS, BUILTIN_COLUMNS, KEYWORDS, SNIPPETS } from './builtins.js';
const LANGUAGE_ID = 'quantquack-dsl';
/**
 * Register DSL language with Monaco Editor
 */
export function registerDSLLanguage(monacoInstance) {
    // Register language
    monacoInstance.languages.register({ id: LANGUAGE_ID });
    // Set language configuration
    monacoInstance.languages.setLanguageConfiguration(LANGUAGE_ID, {
        comments: {
            lineComment: '#',
            blockComment: ['/*', '*/'],
        },
        brackets: [
            ['{', '}'],
            ['[', ']'],
            ['(', ')'],
        ],
        autoClosingPairs: [
            { open: '{', close: '}' },
            { open: '[', close: ']' },
            { open: '(', close: ')' },
            { open: '"', close: '"' },
            { open: "'", close: "'" },
        ],
        surroundingPairs: [
            { open: '{', close: '}' },
            { open: '[', close: ']' },
            { open: '(', close: ')' },
            { open: '"', close: '"' },
            { open: "'", close: "'" },
        ],
        indentationRules: {
            increaseIndentPattern: /^\s*(if|func|stats|input).*$/,
            decreaseIndentPattern: /^\s*(else|end).*$/,
        },
    });
    // Set tokens provider for syntax highlighting
    monacoInstance.languages.setMonarchTokensProvider(LANGUAGE_ID, {
        keywords: KEYWORDS,
        operators: ['=', '>', '<', '>=', '<=', '==', '!=', '+', '-', '*', '/', 'and', 'or', 'not'],
        symbols: /[=><!?:+\-*\/\^%]+/,
        tokenizer: {
            root: [
                // Keywords
                [
                    /\b(if|then|else|input|func|signal|output|stats|by|count|percent|avg|as|int|float|bool|string|true|false)\b/,
                    'keyword',
                ],
                // Comments (DSL uses # for line comments, must come after keywords)
                [/#.*$/, 'comment'],
                // Identifiers
                [/[a-z_$][\w$]*/, 'identifier'],
                // Numbers
                [/\d+\.?\d*/, 'number'],
                // Strings
                [/"/, 'string', '@string'],
                [/'/, 'string', '@stringSingle'],
                // Block comments
                [/\/\*/, 'comment', '@comment'],
                // Operators
                [/[=><!?:+\-*\/\^%]+/, 'operator'],
                // Whitespace
                [/\s+/, 'white'],
            ],
            string: [
                [/[^\\"]+/, 'string'],
                [/\\./, 'string.escape.invalid'],
                [/"/, 'string', '@pop'],
            ],
            stringSingle: [
                [/[^\\']+/, 'string'],
                [/\\./, 'string.escape.invalid'],
                [/'/, 'string', '@pop'],
            ],
            comment: [
                [/[^/*]+/, 'comment'],
                [/\/\*/, 'comment', '@push'],
                [/\*\//, 'comment', '@pop'],
                [/[/*]/, 'comment'],
            ],
        },
    });
    // Register completion provider
    monacoInstance.languages.registerCompletionItemProvider(LANGUAGE_ID, {
        provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: word.startColumn,
                endColumn: word.endColumn,
            };
            const suggestions = [];
            // Add built-in functions
            BUILTIN_FUNCTIONS.forEach((func) => {
                const params = func.params.map((p, idx) => `\${${idx + 1}:${p.name}}`).join(', ');
                const signature = `${func.name}(${func.params.map(p => p.type).join(', ')}): ${func.returnType}`;
                suggestions.push({
                    label: func.name,
                    kind: monacoInstance.languages.CompletionItemKind.Function,
                    detail: signature,
                    documentation: {
                        value: `**${func.name}** - ${func.returnType}\n\n${func.description}\n\n**Parameters:**\n${func.params.map(p => `- \`${p.name}\`: ${p.type}${p.description ? ` - ${p.description}` : ''}`).join('\n')}\n\n${func.example ? `**Example:**\n\`\`\`dsl\n${func.example}\n\`\`\`` : ''}`,
                        isTrusted: true,
                    },
                    insertText: func.name + '(' + params + ')',
                    insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                    range,
                });
            });
            // Add built-in columns
            BUILTIN_COLUMNS.forEach((col) => {
                suggestions.push({
                    label: col.name,
                    kind: monacoInstance.languages.CompletionItemKind.Variable,
                    documentation: {
                        value: `**${col.name}** - ${col.type}\n\n${col.description}`,
                        isTrusted: true,
                    },
                    insertText: col.name,
                    range,
                });
            });
            // Add keywords
            KEYWORDS.forEach((keyword) => {
                suggestions.push({
                    label: keyword,
                    kind: monacoInstance.languages.CompletionItemKind.Keyword,
                    insertText: keyword,
                    range,
                });
            });
            // Add snippets
            SNIPPETS.forEach((snippet) => {
                suggestions.push({
                    label: snippet.name,
                    kind: monacoInstance.languages.CompletionItemKind.Snippet,
                    detail: 'Snippet',
                    documentation: {
                        value: snippet.description,
                        isTrusted: true,
                    },
                    insertText: snippet.body.join('\n'),
                    insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                    range,
                });
            });
            return { suggestions };
        },
    });
    // Register hover provider
    monacoInstance.languages.registerHoverProvider(LANGUAGE_ID, {
        provideHover: (model, position) => {
            const word = model.getWordAtPosition(position);
            if (!word)
                return null;
            const wordText = word.word;
            // Check if it's a built-in function
            const func = BUILTIN_FUNCTIONS.find((f) => f.name === wordText);
            if (func) {
                const signature = `${func.name}(${func.params.map(p => p.type).join(', ')}): ${func.returnType}`;
                return {
                    range: {
                        startLineNumber: position.lineNumber,
                        endLineNumber: position.lineNumber,
                        startColumn: word.startColumn,
                        endColumn: word.endColumn,
                    },
                    contents: [
                        {
                            value: `**${func.name}** - ${func.returnType}\n\n${func.description}\n\n**Parameters:**\n${func.params.map(p => `- \`${p.name}\`: ${p.type}${p.description ? ` - ${p.description}` : ''}`).join('\n')}\n\n${func.example ? `**Example:**\n\`\`\`dsl\n${func.example}\n\`\`\`` : ''}`,
                        },
                    ],
                };
            }
            // Check if it's a built-in column
            const col = BUILTIN_COLUMNS.find((c) => c.name === wordText);
            if (col) {
                return {
                    range: {
                        startLineNumber: position.lineNumber,
                        endLineNumber: position.lineNumber,
                        startColumn: word.startColumn,
                        endColumn: word.endColumn,
                    },
                    contents: [
                        {
                            value: `**${col.name}** - ${col.type}\n\n${col.description}`,
                        },
                    ],
                };
            }
            return null;
        },
    });
    // Register signature help provider
    monacoInstance.languages.registerSignatureHelpProvider(LANGUAGE_ID, {
        signatureHelpTriggerCharacters: ['('],
        provideSignatureHelp: (model, position, token, context) => {
            const textUntilPosition = model.getValueInRange({
                startLineNumber: 1,
                startColumn: 1,
                endLineNumber: position.lineNumber,
                endColumn: position.column,
            });
            // Find the function call at the current position
            const lines = textUntilPosition.split('\n');
            const currentLine = lines[lines.length - 1];
            // Match function name before opening parenthesis
            const match = currentLine.match(/(\w+)\s*\(/);
            if (!match)
                return null;
            const funcName = match[1];
            const func = BUILTIN_FUNCTIONS.find((f) => f.name === funcName);
            if (!func)
                return null;
            // Count commas to determine active parameter
            const lineAfterFunc = currentLine.substring(currentLine.indexOf('('));
            const commasBeforeCursor = (lineAfterFunc.match(/,/g) || []).length;
            const activeParameter = commasBeforeCursor;
            return {
                value: {
                    signatures: [
                        {
                            label: `${func.name}(${func.params.map(p => `${p.name}: ${p.type}`).join(', ')})`,
                            documentation: {
                                value: func.description,
                            },
                            parameters: func.params.map((p) => ({
                                label: `${p.name}: ${p.type}`,
                                documentation: p.description || '',
                            })),
                        },
                    ],
                    activeSignature: 0,
                    activeParameter: Math.min(activeParameter, func.params.length - 1),
                },
                dispose: () => { },
            };
        },
    });
}
/**
 * Get language ID
 */
export function getLanguageId() {
    return LANGUAGE_ID;
}
